import { useEffect, useState } from 'react';

export function useServiceWorker() {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Register service worker in all environments
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then((reg) => {
          console.log('[SW] Service Worker registered', reg);
          setRegistration(reg);

          // Check if there's already a waiting service worker
          if (reg.waiting) {
            console.log('[SW] Update already waiting');
            setUpdateAvailable(true);
          }

          // Check for updates periodically (every 60 seconds)
          const updateInterval = setInterval(() => {
            console.log('[SW] Checking for updates...');
            reg.update().catch(console.error);
          }, 60000);

          // Check for updates when tab becomes visible
          const handleVisibilityChange = () => {
            if (!document.hidden) {
              console.log('[SW] Tab visible, checking for updates...');
              reg.update().catch(console.error);
            }
          };
          document.addEventListener('visibilitychange', handleVisibilityChange);

          // Listen for update found event
          const handleUpdateFound = () => {
            const newWorker = reg.installing;
            console.log('[SW] Update found, new worker installing...');

            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                console.log('[SW] New worker state:', newWorker.state);
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  console.log('[SW] New service worker installed, update available');
                  setUpdateAvailable(true);
                }
              });
            }
          };

          reg.addEventListener('updatefound', handleUpdateFound);

          // Cleanup
          return () => {
            clearInterval(updateInterval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            reg.removeEventListener('updatefound', handleUpdateFound);
          };
        })
        .catch((error) => {
          console.error('[SW] Service Worker registration failed:', error);
        });
    }

    // Handle online/offline status
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return {
    registration,
    isOffline,
    updateAvailable,
  };
}
